import React, { useState, useEffect, useCallback } from 'react';
import './resumeLibrary.css';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../ThemeContext';
import { apiFetch } from '../../api/client';
import { FiFileText, FiTrash2, FiExternalLink, FiPlus, FiLock } from 'react-icons/fi';

function ResumeLibrary() {
  const { darkMode } = useTheme();
  const navigate = useNavigate();

  const [resumes, setResumes] = useState([]);
  const [savedCount, setSavedCount] = useState(0);
  const [savedLimit, setSavedLimit] = useState(0);
  const [tier, setTier] = useState('free');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await apiFetch('/api/resume/list');
      setResumes(data.resumes);
      setSavedCount(data.savedCount);
      setSavedLimit(data.savedLimit);
      setTier(data.tier);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await apiFetch(`/api/resume/${id}`, { method: 'DELETE' });
      setResumes((r) => r.filter((x) => x._id !== id));
      setSavedCount((c) => Math.max(0, c - 1));
    } catch (e) {
      setError(e.message);
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <div className={`libraryContainer ${darkMode ? 'dark-mode' : 'light-mode'}`}>
      <div className="libraryInner">
        <div className="libraryHeader">
          <div>
            <h1 className="libraryTitle">Saved resumes</h1>
            <p className="librarySubtitle">Your tailored resumes, ready to download anytime</p>
          </div>
          {savedLimit > 0 && (
            <span className="savedCountPill">{savedCount} / {savedLimit} saved</span>
          )}
        </div>

        {error && <div className="libraryError">{error}</div>}

        {loading ? (
          <div className="libraryEmpty">Loading…</div>
        ) : tier === 'free' ? (
          <div className="libraryEmpty">
            <FiLock size={26} />
            <p>Saving resumes requires a paid plan.</p>
            <button className="libPillBtn solid" onClick={() => navigate('/pricing')}>View plans</button>
          </div>
        ) : resumes.length === 0 ? (
          <div className="libraryEmpty">
            <FiFileText size={26} />
            <p>No saved resumes yet. Build one and hit Save in the editor.</p>
            <button className="libPillBtn solid" onClick={() => navigate('/app/resume')}>
              <FiPlus size={14} /> Build a resume
            </button>
          </div>
        ) : (
          <div className="libraryGrid">
            {resumes.map((r) => (
              <div className="libraryCard" key={r._id}>
                <div className="libraryCardIcon"><FiFileText size={20} /></div>
                <div className="libraryCardBody">
                  <span className="libraryCardTitle">{r.jobTitle || 'Untitled resume'}</span>
                  <span className="libraryCardSub">
                    {[r.company, r.templateName].filter(Boolean).join(' · ')}
                  </span>
                  <span className="libraryCardDate">{formatDate(r.updatedAt)}</span>
                </div>
                <div className="libraryCardActions">
                  <button className="libPillBtn ghost small" onClick={() => navigate(`/app/editor/${r._id}`)}>
                    <FiExternalLink size={13} /> Open
                  </button>
                  <button
                    className="libIconBtn danger"
                    onClick={() => handleDelete(r._id)}
                    disabled={deletingId === r._id}
                    title="Delete"
                  >
                    <FiTrash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ResumeLibrary;
